import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import ActionsVideoThumbnailNormalXsmall from '@strava/icons/ActionsVideoThumbnailNormalXsmall';

import StravaMediaLightbox from 'components/StravaMediaLightbox';
import { trackV2 } from 'utils/analytics';
import { logError } from 'utils/sentry';

import styles from './MediaThumbnailList.scss';

// How much media we try to keep loaded ahead of where the athlete is in the carousel. Wide enough
// to cover a couple of pages, so navigation stays ahead of the network instead of stalling on the
// last photo while a page is in flight.
const PREFETCH_AHEAD = 20;

// How many pages that add nothing we chase before giving up. A page can still come back empty
// after the server's own filtering - media whose activity has since gone away is dropped as the
// list is built - and an empty page leaves us just as close to the end as before, so the prefetch
// check fires again immediately. Without a ceiling one lightbox open can walk an athlete's entire
// photo history, a request at a time.
const MAX_EMPTY_PAGES = 3;

function MediaThumbnailList({
  isLoggedIn = false,
  items = [],
  viewableCount = 8,
  thumbnailSize = 60,
  category,
  profileType,
  page,
  nextCursor = null,
  hasMore = false,
  athleteId = null,
  pageSize = 10,
  // Doubles as the signal that the caller pages its media in: only a caller that can't count the
  // remainder needs a label for the "more" tile. Callers that set it pass a translated string.
  morePhotosText = null
}) {
  const [allItems, setAllItems] = useState(items);
  const [cursor, setCursor] = useState(nextCursor);
  const [hasMorePhotos, setHasMorePhotos] = useState(hasMore);
  const [isFetching, setIsFetching] = useState(false);

  // `selectedIndex` follows the athlete through the carousel; `openedIndex` stays on the thumbnail
  // they came in by, which is the one the analytics below report on.
  const [lightboxState, setLightboxState] = useState({
    isOpen: false,
    selectedIndex: 0,
    openedIndex: 0
  });

  // Tracked in a ref as well as state: state updates are async, so two navigations in quick
  // succession would both pass an `isFetching` check and request the same page twice.
  const isFetchingRef = useRef(false);
  const emptyPageCountRef = useRef(0);

  // Fetch the next page of photos from the server
  const fetchMorePhotos = useCallback(async () => {
    if (!cursor || !athleteId || isFetchingRef.current || !hasMorePhotos) {
      return;
    }

    isFetchingRef.current = true;
    setIsFetching(true);
    try {
      const response = await fetch(
        `/athletes/${athleteId}/photos?cursor=${encodeURIComponent(
          cursor
        )}&per_page=${pageSize}`,
        {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          credentials: 'same-origin'
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const newItems = data.items || [];

      // A cursor points at a photo rather than a page boundary, so a page can overlap the one
      // before it. Dropping repeats keeps the carousel honest and the React keys unique. Only one
      // request is ever in flight, so `allItems` is what the page is landing on top of.
      const seen = new Set(allItems.map((item) => item.photo_id));
      const unseen = newItems.filter((item) => !seen.has(item.photo_id));

      emptyPageCountRef.current =
        unseen.length === 0 ? emptyPageCountRef.current + 1 : 0;

      if (unseen.length > 0) {
        setAllItems((prev) => [...prev, ...unseen]);
      }
      setCursor(data.next_cursor);
      // A cursor that hasn't moved would have us request the same page forever, and neither will
      // pages that keep arriving with nothing in them.
      setHasMorePhotos(
        data.has_more &&
          data.next_cursor !== cursor &&
          emptyPageCountRef.current < MAX_EMPTY_PAGES
      );
    } catch (error) {
      logError(error);
      // Stop paging. The cursor is unchanged, so leaving this on would have the prefetch effect
      // re-request the same failing page for as long as the athlete stays near the end.
      setHasMorePhotos(false);
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
    }
  }, [cursor, athleteId, hasMorePhotos, pageSize, allItems]);

  // Keep media loaded ahead of the athlete rather than waiting for them to reach the end.
  // `isFetching` is a dependency so this re-checks once a page lands, which chains the next
  // request until the buffer is full - from the moment the lightbox opens, not once they have
  // already walked to the last photo we have.
  useEffect(() => {
    if (!lightboxState.isOpen || !hasMorePhotos || isFetching) {
      return;
    }

    const loadedAhead = allItems.length - lightboxState.selectedIndex - 1;

    if (loadedAhead < PREFETCH_AHEAD) {
      fetchMorePhotos();
    }
  }, [
    lightboxState.isOpen,
    lightboxState.selectedIndex,
    hasMorePhotos,
    isFetching,
    allItems.length,
    fetchMorePhotos
  ]);

  const itemsToDisplay = viewableCount - 1;
  // The tile stands in for everything past the grid, so there has to be something past the grid:
  // either media still to load, or more loaded than the grid can show. It also doubles as a
  // thumbnail, so item `itemsToDisplay` has to exist - which it doesn't when privacy filtering
  // leaves the first page shorter than the grid. Those few photos are better shown in full.
  const hasMoreItems =
    allItems.length > itemsToDisplay &&
    (hasMorePhotos || allItems.length > viewableCount);
  // An owner that pages its media in (the profile) never knows the total, so it hands us a label
  // to use in place of a count. One that renders everything up front (activity detail) passes no
  // label and keeps the "+ N" it always had. Read from the prop rather than from `hasMorePhotos`
  // so the tile doesn't switch wording part way through browsing.
  const isPaginated = Boolean(morePhotosText);
  const adjustedItems = hasMoreItems
    ? allItems.slice(0, itemsToDisplay)
    : allItems;
  const moreCount = allItems.length - itemsToDisplay;

  useEffect(() => {
    if (window.location.pathname.includes('/activities')) {
      // Provided that URL includes a unique videoId param then we can assert that URL is a redirect from BranchIO deep link.
      // Then we can programmatically trigger the Modal (StravaMediaLightbox) and select that video to display.
      // Deep link format => `/activities/<activityId>?videoId=<video_id>`
      const urlParams = new URLSearchParams(window.location.search);
      // Grab unique videoId param from deep link
      const videoId = urlParams.get('videoId');
      // If no videoId param then it's not a deep link so do nothing.
      if (videoId) {
        // Resolved against the initially rendered media only: re-running this as pages load would
        // yank the athlete back to the deep-linked video mid-browse.
        const photoIndex = items.findIndex((item) => item.photo_id === videoId);
        if (photoIndex >= 0) {
          setLightboxState({
            isOpen: true,
            selectedIndex: photoIndex,
            openedIndex: photoIndex
          });
        }
      }
    }
  }, [items]);

  // Analytics for screen enter/exit
  useEffect(() => {
    if (items.length === 0) {
      return;
    }
    const firstItem = items[0];
    const isAdp = page === 'activity_detail';
    const data = {
      category,
      page,
      element: isAdp ? 'media' : 'media_thumbnail',
      properties: {
        athlete_id: firstItem.viewing_athlete_id,
        requested_athlete_id: firstItem.owner_id
      }
    };

    if (isAdp) {
      data.properties.activity_id = firstItem.activity_id;
      data.properties.activity_athlete_id = firstItem.owner_id;
    }

    trackV2({
      ...data,
      action: 'screen_enter'
    });

    window.addEventListener('beforeunload', function() {
      trackV2({
        ...data,
        action: 'screen_exit'
      });
    });
  }, []);

  function handleItemClick(index) {
    setLightboxState({
      isOpen: true,
      selectedIndex: index,
      openedIndex: index
    });
  }

  const onMediaClick = (media) => {
    const data = {
      category,
      page,
      properties: {
        activity_id: media.activity_id,
        activity_athlete_id: media.owner_id,
        athlete_id: media.viewing_athlete_id,
        requested_athlete_id: media.owner_id
      }
    };

    const isImage = media.media_type === 1;
    if (isImage) {
      data.properties.image_id = media.photo_id;
    } else {
      data.properties.video_id = media.photo_id;
    }

    // Photo / Video
    const adpElement = isImage ? 'image' : 'video';
    const profileElement = isImage ? 'photo_thumbnail' : 'video_thumbnail';

    trackV2({
      ...data,
      action: 'click',
      element: category === 'activity_detail' ? adpElement : profileElement
    });
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <MediaList>
        {adjustedItems.map((item, index) => (
          <MediaListItem
            key={item.photo_id}
            style={{ height: thumbnailSize, width: thumbnailSize }}
          >
            <Item
              isInteractive={isLoggedIn}
              onClick={() => handleItemClick(index)}
            >
              <img src={item.thumbnail} alt="" />
              {item.media_type === 2 ? <VideoIndicator /> : null}
            </Item>
          </MediaListItem>
        ))}

        {hasMoreItems ? (
          <MediaListItem
            style={{ height: thumbnailSize, width: thumbnailSize }}
          >
            <MoreItem
              isInteractive={isLoggedIn}
              onClick={() => handleItemClick(itemsToDisplay)}
            >
              <img src={allItems[itemsToDisplay].thumbnail} alt="" />
              {allItems[itemsToDisplay].media_type === 2 ? (
                <VideoIndicator />
              ) : null}
              <div className={styles.count}>
                {isPaginated ? morePhotosText : `+ ${moreCount}`}
              </div>
            </MoreItem>
          </MediaListItem>
        ) : null}
      </MediaList>

      {lightboxState.isOpen ? (
        <StravaMediaLightbox
          selectedPhotoIndex={lightboxState.selectedIndex}
          photoList={allItems}
          hasMore={hasMorePhotos}
          onPhotoIndexChange={(photoIndex) => {
            setLightboxState((prev) => ({
              ...prev,
              selectedIndex: photoIndex
            }));
          }}
          onMount={() => {
            onMediaClick(allItems[lightboxState.openedIndex]);
          }}
          onCloseRequest={() => {
            setLightboxState((prev) => ({ ...prev, isOpen: false }));
          }}
          analyticProps={{
            page,
            feedType: profileType,
            entityId: allItems[lightboxState.openedIndex].activity_id
          }}
        />
      ) : null}
    </>
  );
}

MediaThumbnailList.propTypes = {
  isLoggedIn: PropTypes.bool,
  items: PropTypes.arrayOf(
    // TODO: Look into if we have have this shared somehow
    PropTypes.shape({
      media_type: PropTypes.number.isRequired,
      photo_id: PropTypes.string.isRequired,
      large: PropTypes.string.isRequired,
      thumbnail: PropTypes.string.isRequired,
      caption_escaped: PropTypes.string,
      viewing_athlete_id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ]),
      owner_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      editable: PropTypes.bool,
      activity_id: PropTypes.string
    })
  ),
  viewableCount: PropTypes.number,
  thumbnailSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  category: PropTypes.string.isRequired,
  profileType: PropTypes.string.isRequired,
  page: PropTypes.string.isRequired,
  nextCursor: PropTypes.string,
  hasMore: PropTypes.bool,
  athleteId: PropTypes.number,
  pageSize: PropTypes.number,
  morePhotosText: PropTypes.string
};

function Item({ isInteractive, children, onClick }) {
  return isInteractive ? (
    <MediaListItemButton onClick={onClick} onKeyPress={onClick}>
      {children}
    </MediaListItemButton>
  ) : (
    <div className={styles.buttonLoggedOut}>{children}</div>
  );
}

Item.propTypes = {
  isInteractive: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired
};

function MoreItem({ isInteractive, children, onClick }) {
  return isInteractive ? (
    <MediaListItemButton
      className={styles.seeMoreButton}
      onClick={onClick}
      onKeyPress={onClick}
    >
      {children}
    </MediaListItemButton>
  ) : (
    <div
      className={clsx(styles.buttonLoggedOut, styles.seeMoreButtonLoggedOut)}
    >
      {children}
    </div>
  );
}

MoreItem.propTypes = {
  isInteractive: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired
};

/**
 * MediaList
 */
function MediaList({ className, ...props }) {
  return <ul {...props} className={clsx(styles.list, className)} />;
}

MediaList.propTypes = {
  className: PropTypes.string
};

/**
 * MediaListItem
 */

function MediaListItem({ className, ...props }) {
  return <li {...props} className={clsx(styles.item, className)} />;
}

MediaListItem.propTypes = {
  className: PropTypes.string
};

/**
 * MediaListItemButton
 */

function MediaListItemButton({ className, ...props }) {
  return (
    <div
      {...props}
      role="button"
      className={clsx(styles.button, className)}
      tabIndex="0"
    />
  );
}

MediaListItemButton.propTypes = {
  className: PropTypes.string
};

function VideoIndicator() {
  return (
    <div className={styles.videoIndicator} data-testid="video-indicator">
      <ActionsVideoThumbnailNormalXsmall />
    </div>
  );
}

export default MediaThumbnailList;
